/**
 * API Configuration
 * ==================
 * Centralised backend URL resolution. Mirrors the same precedence as
 * `lib/apiClient.js`:
 *
 *   1. `process.env.REACT_APP_BACKEND_URL`  — production / preview
 *   2. `window.location.origin`              — same-domain dev fallback
 *
 * The Kubernetes ingress redirects `/api/*` to the FastAPI backend, so
 * when the env var is set both options point at the same host. The env
 * var wins because the platform contract (every page must hit one
 * deterministic backend URL) needs an explicit value that's identical
 * across SSR, hydration, and runtime.
 */

/**
 * Get the correct backend URL.
 * Prefers `REACT_APP_BACKEND_URL`; falls back to `window.location.origin`
 * so dev / local runs don't need the env var set.
 *
 * Apex/www guard: if the configured backend host is the SAME registrable
 * domain as the page (differing only by a `www.` prefix), we use the
 * current page origin instead. This prevents cross-origin "Failed to
 * fetch" when, e.g., the build baked `https://www.corvana.com.au` but the
 * user loads `https://corvana.com.au` (the apex) — the www host only
 * 308-redirects to the apex, which breaks credentialed CORS/CSP requests.
 */
const stripWww = (host) => (host || '').replace(/^www\./i, '');

export const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (envUrl) {
      try {
        const envHost = new URL(envUrl).hostname;
        const curHost = window.location.hostname;
        // Same site, host only differs by www prefix → use the page origin
        // so the request stays same-origin (avoids redirect + CSP blocks).
        if (stripWww(envHost) === stripWww(curHost) && envHost !== curHost) {
          return origin;
        }
      } catch {
        /* malformed env URL — fall through to env/origin below */
      }
    }
    return envUrl || origin;
  }

  return envUrl || '';
};

/**
 * The backend API URL to use for all API calls.
 *
 * Replace local `const API_URL = ...` patterns with:
 *   `import { API_URL } from '@/config/apiConfig';`
 */
export const API_URL = getBackendUrl();

/**
 * Check if we're running in production environment.
 */
export const isProduction = () => {
  // Use NODE_ENV which is set during build
  if (process.env.NODE_ENV === 'production') {
    return true;
  }
  // Check for non-localhost hostnames as production
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return !hostname.includes('localhost') && !hostname.includes('127.0.0.1');
  }
  return false;
};

export default {
  API_URL,
  getBackendUrl,
  isProduction
};
